import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Sparkles, ArrowRight, Mail, Phone, MapPin, ExternalLink } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import SEO, { SITE_URL } from "../SEO";
import { Overline } from "../Reveal";
import { Button } from "../ui/button";
import { COMPANY, PRODUCT_FAMILIES, PRODUCTS_KB, USER_TYPES, INDUSTRIES } from "../../data/knowledge";
import { PLANS, planDisplayName } from "../../data/content";
import { productUrl } from "../../lib/finder";
import { ORGANIZATION_NODE } from "../../lib/schema";
import { FaqSection } from "../FaqList";
import { ABOUT_FAQS, SERVICE_AREA, REFUND_SUMMARY } from "../../data/pageFaqs";

const PATH = "/about";

// Same Organization/LocalBusiness entity (and @id) as the homepage graph.
// The address, phone and email it declares are all visible on this page.
const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "@id": `${SITE_URL}${PATH}#page`,
  url: `${SITE_URL}${PATH}`,
  name: "About MagicBolt AI",
  isPartOf: { "@id": `${SITE_URL}/#website` },
  mainEntity: ORGANIZATION_NODE,
};

function Block({ id, overline, title, children, tinted }) {
  return (
    <section id={id} className={`py-14 md:py-16 ${tinted ? "bg-white border-y border-[#E6E9F1]" : ""}`}>
      <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-8">
        <Overline>{overline}</Overline>
        <h2 className="mt-3 text-2xl md:text-3xl font-bold tracking-tighter text-[#0B1533]">{title}</h2>
        <div className="mt-6">{children}</div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-[#F4F6FB]" data-testid="about-page">
      <SEO
        title="About MagicBolt AI - White-Label AI Automation for Agencies"
        description="Who MagicBolt AI is, what the platform does, who it is for and how the white-label reseller model works. MagicBolt Private Limited, Bhopal, India."
        path={PATH}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "About", path: PATH },
        ]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(JSON_LD)}</script>
      </Helmet>
      <Navbar />

      <section className="relative pt-32 pb-14 overflow-hidden bg-white border-b border-[#E6E9F1]">
        <div className="mb-blob mb-blob-orange mb-anim-a w-[360px] h-[360px] -top-24 right-0" />
        <div className="absolute inset-0 mb-grid-lines opacity-60" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#E6E9F1] bg-white px-4 py-1.5 mb-6 shadow-[0_2px_10px_rgba(11,21,51,0.05)]">
              <Sparkles className="w-3.5 h-3.5 text-[#FF6B00]" />
              <span className="text-xs font-medium text-[#475569]">{COMPANY.legalName}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-[1.05] text-[#0B1533]">
              What is <span className="mb-gradient-text">MagicBolt AI</span>?
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#475569] leading-relaxed text-left sm:text-center">
              {COMPANY.summary}
            </p>
          </motion.div>
        </div>
      </section>

      <Block id="how-it-works" overline="Business model" title="How does MagicBolt AI work?">
        <p className="text-base text-[#475569] leading-relaxed max-w-3xl">{COMPANY.businessModel}</p>
        <p className="mt-4 text-base text-[#475569] leading-relaxed max-w-3xl">
          The resellable bundle is called <Link to="/agency-os" className="font-semibold text-[#FF6B00] hover:underline">{COMPANY.platformName}</Link>.
          See <Link to="/how-agencies-earn" className="font-semibold text-[#FF6B00] hover:underline">how agencies earn</Link> from it
          and the <Link to="/pricing" className="font-semibold text-[#FF6B00] hover:underline">current plans</Link>:{" "}
          {PLANS.map((p) => planDisplayName(p.name)).join(", ")} (Unlimited) and Channel Based plans.
        </p>
      </Block>

      <Block id="products" overline="Product ecosystem" title="What products does MagicBolt AI offer?" tinted>
        <div className="grid gap-6 md:grid-cols-2">
          {PRODUCT_FAMILIES.map((fam) => (
            <div key={fam.id} className="rounded-2xl border border-[#E6E9F1] bg-[#F4F6FB] p-6">
              <h3 className="text-lg font-bold text-[#0B1533]">{fam.name}</h3>
              <p className="mt-1.5 text-sm text-[#475569] leading-relaxed">{fam.description}</p>
              <dl className="mt-4 space-y-3">
                {PRODUCTS_KB.filter((p) => p.family === fam.id).map((p) => (
                  <div key={p.id}>
                    <dt>
                      <Link to={productUrl(p.id)} className="text-sm font-semibold text-[#FF6B00] hover:underline">
                        {p.name}
                      </Link>
                    </dt>
                    <dd className="text-sm text-[#475569] leading-relaxed">{p.definition}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </Block>

      <Block id="audience" overline="Who it's for" title="Who is MagicBolt AI for?">
        <ul className="grid gap-4 md:grid-cols-2">
          {USER_TYPES.map((u) => (
            <li key={u.id} className="rounded-2xl border border-[#E6E9F1] bg-white p-5">
              <div className="font-semibold text-[#0B1533]">{u.name}</div>
              <p className="mt-1 text-sm text-[#475569] leading-relaxed">{u.description}</p>
            </li>
          ))}
        </ul>
        <p className="mt-8 text-sm text-[#475569] leading-relaxed">
          <span className="font-semibold text-[#0B1533]">Industries commonly served: </span>
          {INDUSTRIES.map((i) => i.name).join(", ")}.{" "}
          <Link to="/product-finder#by-industry" className="font-semibold text-[#FF6B00] hover:underline">
            See which tools fit each industry
          </Link>
          .
        </p>
      </Block>

      <Block id="service-area" overline="Where we operate" title="Where does MagicBolt AI operate?" tinted>
        <p className="text-base text-[#475569] leading-relaxed max-w-3xl">{SERVICE_AREA}</p>
      </Block>

      <Block id="guarantees" overline="Policies" title="What refund terms apply?">
        <p className="text-base text-[#475569] leading-relaxed max-w-3xl">{REFUND_SUMMARY}</p>
        <p className="mt-4 text-sm">
          <Link to="/refund-policy" className="font-semibold text-[#FF6B00] hover:underline">Read the full Refund Policy</Link>
          <span className="text-[#94A3B8]"> · </span>
          <Link to="/terms-conditions" className="font-semibold text-[#FF6B00] hover:underline">Terms &amp; Conditions</Link>
        </p>
      </Block>

      <Block id="contact" overline="Company details" title="How can I contact MagicBolt AI?" tinted>
        <div className="grid gap-6 md:grid-cols-2">
          <ul className="space-y-3 text-sm text-[#475569]">
            <li className="flex gap-3">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[#FF6B00]" />
              <span>
                <span className="font-semibold text-[#0B1533]">{COMPANY.legalName}</span>
                <br />
                {COMPANY.address.streetAddress}, {COMPANY.address.addressLocality}, {COMPANY.address.addressRegion}{" "}
                {COMPANY.address.postalCode}, India
              </span>
            </li>
            <li className="flex gap-3">
              <Mail className="h-4 w-4 mt-0.5 shrink-0 text-[#FF6B00]" />
              <a href={`mailto:${COMPANY.email}`} className="hover:text-[#0B1533]">{COMPANY.email}</a>
            </li>
            <li className="flex gap-3">
              <Phone className="h-4 w-4 mt-0.5 shrink-0 text-[#FF6B00]" />
              <a href={`tel:${COMPANY.phone.replace(/-/g, "")}`} className="hover:text-[#0B1533]">{COMPANY.phone}</a>
            </li>
          </ul>
          <ul className="space-y-2 text-sm">
            {COMPANY.officialLinks.map((l) => (
              <li key={l.url}>
                <a href={l.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-semibold text-[#FF6B00] hover:underline">
                  {l.label} <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </li>
            ))}
            <li className="pt-2 text-[#475569]">
              Machine-readable: <a href="/products.json" className="font-semibold text-[#FF6B00] hover:underline">products.json</a>
              {" · "}
              <a href="/llms.txt" className="font-semibold text-[#FF6B00] hover:underline">llms.txt</a>
            </li>
          </ul>
        </div>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <Button size="lg" className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold rounded-lg h-12 px-7" asChild>
            <Link to="/product-finder">Find the right product <ArrowRight className="ml-2 w-4 h-4" /></Link>
          </Button>
          <Button size="lg" variant="outline" className="border-[#CBD2E0] bg-white text-[#0B1533] font-semibold rounded-lg h-12 px-7" asChild>
            <Link to="/contact">Contact us</Link>
          </Button>
        </div>
      </Block>

      <FaqSection title="Frequently asked questions about the company" items={ABOUT_FAQS} />

      <Footer />
    </div>
  );
}
